import { TimePeriod } from "./TimePeriodSelector";
import { getPeriodCategoryKeys } from "./axis";

// Filter options for blood glucose (kept, but no mock/sample data)
export const BLOOD_GLUCOSE_FILTERS = [
  "All",
  "Fasting",
  "Post-Breakfast",
  "Pre-Lunch",
  "Post-Lunch",
  "Pre-Dinner",
  "Post-Dinner",
  "3 AM",
  "Random",
];

export const normalizeDateForPeriod = (
  period: TimePeriod,
  date: Date
): Date => {
  const normalizedDate = new Date(date);

  switch (period) {
    case "day":
      normalizedDate.setHours(0, 0, 0, 0);
      break;
    case "week": {
      const dayOfWeek = normalizedDate.getDay();
      normalizedDate.setDate(normalizedDate.getDate() - dayOfWeek);
      normalizedDate.setHours(0, 0, 0, 0);
      break;
    }
    case "month":
      normalizedDate.setDate(1);
      normalizedDate.setHours(0, 0, 0, 0);
      break;
    case "year":
      normalizedDate.setMonth(0, 1);
      normalizedDate.setHours(0, 0, 0, 0);
      break;
  }

  return normalizedDate;
};

export const getPeriodBounds = (
  period: TimePeriod,
  date: Date
): { start: Date; end: Date } => {
  const start = normalizeDateForPeriod(period, date);
  const end = new Date(start);

  switch (period) {
    case "day":
      end.setHours(23, 59, 59, 999);
      break;
    case "week": {
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case "month":
      end.setMonth(end.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case "year":
      end.setMonth(11, 31);
      end.setHours(23, 59, 59, 999);
      break;
  }

  return { start, end };
};

export const shiftDateByPeriod = (
  period: TimePeriod,
  date: Date,
  amount: number
): Date => {
  const shiftedDate = normalizeDateForPeriod(period, date);

  switch (period) {
    case "day":
      shiftedDate.setDate(shiftedDate.getDate() + amount);
      break;
    case "week":
      shiftedDate.setDate(shiftedDate.getDate() + amount * 7);
      break;
    case "month":
      shiftedDate.setMonth(shiftedDate.getMonth() + amount, 1);
      break;
    case "year":
      shiftedDate.setFullYear(shiftedDate.getFullYear() + amount, 0, 1);
      break;
  }

  return normalizeDateForPeriod(period, shiftedDate);
};

export const isCurrentPeriod = (
  period: TimePeriod,
  date: Date,
  today: Date = new Date()
): boolean =>
  normalizeDateForPeriod(period, date).getTime() ===
  normalizeDateForPeriod(period, today).getTime();

export const isFuturePeriod = (
  period: TimePeriod,
  date: Date,
  today: Date = new Date()
): boolean =>
  normalizeDateForPeriod(period, date).getTime() >
  normalizeDateForPeriod(period, today).getTime();

// Helper function to get date range based on period (matching Flutter implementation)
export const getDateRange = (period: TimePeriod, date: Date) => {
  const { start, end } = getPeriodBounds(period, date);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
};

// Generic data item interface for tracker charts
interface TrackerDataItem {
  value: number;
  date: string;
  time: string;
}

// Generic response interface for tracker charts
interface TrackerResponse {
  data: TrackerDataItem[];
  y_axis?: { min: number; max: number };
}

// Chart data result interfaces
interface DayChartData {
  xValue: number;
  value: number;
  time: string;
  date: string;
}

interface PeriodChartData {
  categoryKey: string;
  value: number | null;
  date: string;
}

type ChartData = DayChartData[] | PeriodChartData[];

/**
 * Shared utility function to process chart data for simple tracker charts.
 * This function handles the common data processing logic used by Weight, Waist,
 * Temperature, Respiratory Rate, and Hip charts.
 *
 * @param data - The tracker response data
 * @param selectedPeriod - The selected time period (day, week, month, year)
 * @param currentDate - The current date for filtering
 * @returns Processed chart data ready for display
 */
export const processSimpleTrackerChartData = (
  data: TrackerResponse | null,
  selectedPeriod: TimePeriod,
  currentDate: Date
): ChartData => {
  // Check if we have data to display
  const hasData = data && data.data && data.data.length > 0;

  if (!hasData) {
    // Return empty data structure with all categories for week/month/year views
    if (selectedPeriod === "day") {
      return [];
    }

    const allCategories: string[] = getPeriodCategoryKeys(
      selectedPeriod,
      currentDate
    );

    return allCategories.map((key) => ({
      categoryKey: key,
      value: null,
      date: key,
    }));
  }

  const rawData = data.data;

  if (selectedPeriod === "day") {
    // For day view, show individual data points with time
    // Filter data to only show entries for the current date
    // Use local date instead of UTC to avoid timezone issues
    const currentDateStr = currentDate.toLocaleDateString("en-CA"); // Returns YYYY-MM-DD format

    // More robust date filtering - try multiple date formats
    const dayData = rawData.filter((item) => {
      // Try exact match first
      if (item.date === currentDateStr) {
        return true;
      }

      // Try parsing the item date and comparing
      try {
        const itemDate = new Date(item.date);
        const itemDateStr = itemDate.toLocaleDateString("en-CA");
        if (itemDateStr === currentDateStr) {
          return true;
        }
      } catch (e) {
        // Ignore parsing errors
      }

      return false;
    });

    if (dayData.length === 0) {
      return []; // No data for this day
    }

    const processedData = dayData
      .map((item) => {
        // Parse the time string (e.g., "11:26 PM" or "17:53")
        let hour = 0;
        let minute = 0;

        if (item.time.includes("AM") || item.time.includes("PM")) {
          // Handle 12-hour format like "11:26 PM"
          const timeMatch = item.time.match(/(\d+):(\d+)\s*(AM|PM)/);
          if (timeMatch) {
            hour = parseInt(timeMatch[1]);
            minute = parseInt(timeMatch[2]);
            const period = timeMatch[3];

            if (period === "PM" && hour !== 12) {
              hour += 12;
            } else if (period === "AM" && hour === 12) {
              hour = 0;
            }
          }
        } else {
          // Handle 24-hour format like "17:53"
          const timeMatch = item.time.match(/(\d+):(\d+)/);
          if (timeMatch) {
            hour = parseInt(timeMatch[1]);
            minute = parseInt(timeMatch[2]);
          }
        }

        // Create timestamp for the current date with the parsed time
        const timestamp = new Date(currentDate);
        timestamp.setHours(hour, minute, 0, 0);

        const result = {
          xValue: timestamp.getTime(),
          value: item.value,
          time: item.time,
          date: item.date,
        };

        return result;
      })
      .sort((a, b) => a.xValue - b.xValue); // Sort by time

    return processedData;
  } else {
    // For week/month/year views, create line chart data
    const groupedData = new Map<string, number[]>();

    rawData.forEach((item) => {
      let key = "";
      if (selectedPeriod === "week") {
        const date = new Date(item.date);
        const dayOfWeek = date.getDay();
        const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        key = dayNames[dayOfWeek];
      } else if (selectedPeriod === "month") {
        const date = new Date(item.date);
        key = date.getDate().toString();
      } else if (selectedPeriod === "year") {
        const date = new Date(item.date);
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        key = monthNames[date.getMonth()];
      }

      if (key) {
        if (!groupedData.has(key)) {
          groupedData.set(key, []);
        }
        groupedData.get(key)!.push(item.value);
      }
    });

    // Convert grouped data to line chart format (average value for each period)
    const result: PeriodChartData[] = [];

    if (selectedPeriod === "week") {
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      dayNames.forEach((day) => {
        const values = groupedData.get(day) || [];
        if (values.length > 0) {
          const avgValue =
            values.reduce((sum, val) => sum + val, 0) / values.length;
          result.push({
            categoryKey: day,
            value: Math.round(avgValue * 10) / 10, // Round to 1 decimal place
            date: day,
          });
        } else {
          result.push({
            categoryKey: day,
            value: null,
            date: day,
          });
        }
      });
    } else if (selectedPeriod === "month") {
      const { startDate, endDate } = getDateRange("month", currentDate);
      const start = new Date(startDate);
      const end = new Date(endDate);
      const daysInMonth = end.getDate();

      for (let day = 1; day <= daysInMonth; day++) {
        const key = day.toString();
        const values = groupedData.get(key) || [];
        if (values.length > 0) {
          const avgValue =
            values.reduce((sum, val) => sum + val, 0) / values.length;
          result.push({
            categoryKey: key,
            value: Math.round(avgValue * 10) / 10, // Round to 1 decimal place
            date: key,
          });
        } else {
          result.push({
            categoryKey: key,
            value: null,
            date: key,
          });
        }
      }
    } else if (selectedPeriod === "year") {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      monthNames.forEach((month) => {
        const values = groupedData.get(month) || [];
        if (values.length > 0) {
          const avgValue =
            values.reduce((sum, val) => sum + val, 0) / values.length;
          result.push({
            categoryKey: month,
            value: Math.round(avgValue * 10) / 10, // Round to 1 decimal place
            date: month,
          });
        } else {
          result.push({
            categoryKey: month,
            value: null,
            date: month,
          });
        }
      });
    }

    return result;
  }
};
